import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import { PageTitle, Card, Pill, Section, Kpi, fmt } from '../components/ui-kit';
import { ShieldCheck } from 'lucide-react';

interface Framework {
  id: string;
  name: string;
  coverage_pct: number;
  controls_total: number;
  controls_ok: number;
  last_audit?: string;
}

export default function Compliance() {
  const { getToken } = useXyqarAuth();
  const [frameworks, setFrameworks] = React.useState<Framework[] | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const t = await getToken().catch(() => '');
        const j = await apiGet<{ frameworks: Framework[] }>('/api/v1/admin/compliance', t);
        setFrameworks(j.frameworks ?? []);
      } catch {
        setFrameworks([]);
      }
    })();
  }, [getToken]);

  const total = frameworks?.length ?? 0;
  const live = frameworks?.filter((f) => f.coverage_pct >= 90).length ?? 0;
  const inFlight = frameworks?.filter((f) => f.coverage_pct >= 70 && f.coverage_pct < 90).length ?? 0;
  const gap = frameworks?.filter((f) => f.coverage_pct < 70).length ?? 0;
  const totalCtrls = frameworks?.reduce((acc, f) => acc + f.controls_total, 0) ?? 0;
  const okCtrls = frameworks?.reduce((acc, f) => acc + f.controls_ok, 0) ?? 0;
  const overallPct = totalCtrls > 0 ? Math.round((okCtrls / totalCtrls) * 100) : 0;

  return (
    <div>
      <PageTitle
        kicker="Regulatory"
        title="Compliance posture"
        right={
          <div className="flex items-center gap-2 text-[10px] text-white/40 font-mono uppercase">
            <ShieldCheck className="h-3 w-3" /> control mapping · internal posture
          </div>
        }
      />

      <Section
        title="Posture overview"
        hint="Internal control-mapping view across priority frameworks. This is an operational estimate, not an external certification statement."
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi accent="green" label="Frameworks live" value={fmt(live)} hint={`of ${total}`} />
          <Kpi accent="amber" label="In flight" value={fmt(inFlight)} hint="70 – 89% coverage" />
          <Kpi accent={gap > 0 ? 'red' : undefined} label="Gap analysis" value={fmt(gap)} hint="below 70%" />
          <Kpi label="Overall controls" value={`${overallPct}%`} hint={`${okCtrls}/${totalCtrls}`} />
        </div>
      </Section>

      <Section title="Frameworks" hint="These percentages reflect current internal mapping status and should be backed by evidence before being treated as audit-ready.">
        {frameworks === null && <Card>Loading…</Card>}
        {frameworks && frameworks.length === 0 && (
          <Card>
            <p className="text-white/60 text-sm">No framework configured yet.</p>
            <p className="text-white/40 text-xs mt-1">
              Map controls and attach evidence in the Auditor portal to turn this posture view into an auditable one.
            </p>
          </Card>
        )}
        {frameworks && frameworks.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {frameworks.map((f) => {
              const k = f.coverage_pct >= 90 ? 'green' : f.coverage_pct >= 70 ? 'amber' : 'red';
              return (
                <Card key={f.id}>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-bold text-white">{f.name}</p>
                    <Pill kind={k}>{f.coverage_pct}%</Pill>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden mb-2">
                    <div
                      className={
                        'h-full ' +
                        (k === 'green' ? 'bg-green-400' : k === 'amber' ? 'bg-amber-400' : 'bg-red-400')
                      }
                      style={{ width: `${f.coverage_pct}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] text-white/40 font-mono uppercase">
                    <span>
                      {f.controls_ok}/{f.controls_total} controls
                    </span>
                    {f.last_audit && (
                      <span>last audit {new Date(f.last_audit).toLocaleDateString()}</span>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}
